import { HStack, Icon, Text, chakra } from '@chakra-ui/react';
import type { IconType } from 'react-icons';

export interface FilterChipOption<T extends string = string> {
    value: T;
    /** Plain text: it is the visible label and the accessible name both. */
    label: string;
    icon?: IconType;
}

export interface FilterChipsProps<T extends string = string> {
    value: T;
    onChange: (value: T) => void;
    options: readonly FilterChipOption<T>[];
}

const Chip = chakra('button', {
    base: {
        display: 'flex',
        alignItems: 'center',
        textAlign: 'left',
        cursor: 'pointer',
        minWidth: 0,
    },
});

/**
 * A row of filter chips, one selected.
 *
 * The calendar's chip row, shared with the ledger so a member reads the same
 * control on both screens. Distinct from `PillFilters` (charcoal pills with
 * counts, sitting on the canvas): these are gold-wash chips inside a card,
 * each naming its filter with a glyph.
 */
export function FilterChips<T extends string = string>({ value, onChange, options }: FilterChipsProps<T>) {
    return (
        <HStack gap={1.5} rowGap={1.5} flexWrap="wrap">
            {options.map((option) => {
                const on = option.value === value;

                return (
                    <Chip
                        type="button"
                        key={option.value}
                        gap={1.5}
                        px={3}
                        py={1}
                        borderRadius="full"
                        borderWidth="1px"
                        borderColor={on ? 'gold.hairline' : 'border.chrome'}
                        bg={on ? 'gold.wash' : 'bg.panel'}
                        color={on ? 'gold.ink' : 'fg.nav'}
                        flexShrink={0}
                        cursor="pointer"
                        aria-pressed={on}
                        aria-label={option.label}
                        onClick={() => onChange(option.value)}
                        _hover={{ borderColor: 'gold.hairline' }}
                    >
                        {option.icon && <Icon as={option.icon} boxSize="13px" />}
                        <Text fontSize="12.5px" fontWeight="semibold">
                            {option.label}
                        </Text>
                    </Chip>
                );
            })}
        </HStack>
    );
}
